import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ShiftCardWrapper } from "@src/appV2/redesign/Shift/Card/Wrapper";
import { BookingCardContentWrapper } from "@src/appV2/redesign/Shift/CardContentWrapper";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";

import { useGetShiftIdWithOrientationRequired } from "../../Shift/OrientationBlock/useGetShiftIdWithOrientationRequired";
import { type ShiftBlockWithMappedDetails } from "./api/useFetchPaginatedShiftBlocks";
import { SHIFT_BLOCKS_PATH } from "./paths";
import { PriorityAccessAlert } from "./PriorityAccessAlert";
import { ShiftBlockFacilityDetails } from "./ShiftBlockFacilityDetails";
import { ShiftBlockHeader } from "./ShiftBlockHeader";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface ShiftBlockItemProps {
  shiftBlock: ShiftBlockWithMappedDetails;
  workerTimezone: string;
  shouldShowFacilityDetails: boolean;
  showStartEndTime?: boolean;
  isDetailView?: boolean;
}

export function ShiftBlockItem(props: ShiftBlockItemProps) {
  const {
    shiftBlock,
    workerTimezone,
    shouldShowFacilityDetails,
    showStartEndTime,
    isDetailView = false,
  } = props;
  const { id: shiftBlockId, facility, isShiftBlockAssigned, priorityAccess } = shiftBlock;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: facility.id,
  });

  const history = useHistory();
  const { path: currentRouterUrl } = useRouteMatch();
  const shiftIdWithOrientationRequired = useGetShiftIdWithOrientationRequired();

  const handleViewBlockDetails = () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.VIEW_BLOCK_DETAILS_TAPPED,
      shiftBlockId,
      facilityId: facility.id,
      location: currentRouterUrl,
    });
    const path = generatePath(`${RootPaths.APP_V2_HOME}/${SHIFT_BLOCKS_PATH}/:shiftBlockId`, {
      shiftBlockId,
    });

    const params = new URLSearchParams();

    if (isDefined(shiftIdWithOrientationRequired)) {
      params.set("shiftIdWithOrientationRequired", shiftIdWithOrientationRequired);
    }

    history.push({
      pathname: path,
      search: `?${params.toString()}`,
    });
  };

  const handleFacilityClick = () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.FACILITY_PROFILE_CARD_TAPPED,
      facilityId: facility.id,
      isShiftBlockAssigned,
      location: currentRouterUrl,
    });
    const facilityPath = generatePath(`${RootPaths.HOME}/facility/:facilityId`, {
      facilityId: facility.id,
    });
    history.push(`${facilityPath}?shiftBookingType=block-booking`);
  };

  return (
    <ShiftCardWrapper sx={{ minHeight: "auto" }}>
      <CardActionArea disabled={isDetailView} onClick={handleViewBlockDetails}>
        <BookingCardContentWrapper sx={{ gap: 2 }}>
          <ShiftBlockHeader
            shiftBlockWithDetails={shiftBlock}
            workerTimezone={workerTimezone}
            mandatoryBreakDurationInMinutes={mandatoryBreakDurationInMinutes}
            showStartEndTime={showStartEndTime}
          />
          {isDefined(priorityAccess) && isDefined(priorityAccess.priorityTill) && (
            <PriorityAccessAlert priorityAccessTill={priorityAccess.priorityTill} />
          )}
        </BookingCardContentWrapper>
      </CardActionArea>
      {shouldShowFacilityDetails && (
        <CardActionArea onClick={handleFacilityClick}>
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.tertiary,
              borderTop: `1px solid ${theme.palette.border?.primary ?? theme.palette.divider}`,
              paddingX: 6,
              paddingY: 4,
            })}
          >
            <ShiftBlockFacilityDetails facility={facility} />
          </Box>
        </CardActionArea>
      )}
    </ShiftCardWrapper>
  );
}
